import type { FC } from 'react';

import useRole from '@zen/Auth/hooks/useRole';
import useGlobalPermissions from '@zen/Auth/useGlobalPermissions';
import { PurchaseOrderTableVersion, usePurchaseOrderTableVersion } from '@zen/Components/PurchaseOrderTableBanner';
import { useFeatureIsOn } from '@zen/FeatureFlag';
import { Role } from '@zen/Networks/types';

import SidebarLinks from '../components/SidebarLinks';
import type { SidebarCounters } from '../types';
import { getConfig } from './config';
import type { CustomerLinksFeatureFlags } from './types';

interface Props {
  counters: SidebarCounters;
}

const CustomerLinks: FC<Props> = ({ counters }) => {
  const isAskZenseiEnabled: boolean = useFeatureIsOn('ask-zensei');
  const isFinancialsEnabled: boolean = useFeatureIsOn('frontend-financials');
  const isFreightSpendEnabled: boolean = useFeatureIsOn('freight-spend');
  const isNewPurchaseOrderTableEnabled: boolean = useFeatureIsOn('new-po-table');
  const isRateCardsEnabled: boolean = useFeatureIsOn('rate-cards');
  const isUtilisationEnabled: boolean = useFeatureIsOn('utilisation');

  const role = useRole();
  const { tableVersion } = usePurchaseOrderTableVersion();
  const { check } = useGlobalPermissions();

  const purchaseOrderTableVersion: PurchaseOrderTableVersion = isNewPurchaseOrderTableEnabled
    ? tableVersion
    : PurchaseOrderTableVersion.CLASSIC;
  const canViewApiConsole = check('apiConsole.canAccessRoute') || role === Role.AGENT_FORWARDER;
  const canAccessFreightSpend = check('insights.freightSpendCanAccessRoute');

  const featureFlags: CustomerLinksFeatureFlags = {
    isAskZenseiEnabled,
    isFinancialsEnabled,
    isFreightSpendEnabled,
    isRateCardsEnabled,
    isUtilisationEnabled
  };

  const config = getConfig(canViewApiConsole, canAccessFreightSpend, featureFlags, role, purchaseOrderTableVersion);

  return <SidebarLinks config={config} counters={counters} />;
};

export default CustomerLinks;
